import React, { createContext, useContext, useState, useEffect } from 'react';
import { getBackendURL } from '../utils/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [customerToken, setCustomerToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth on mount
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check admin auth
      const storedToken = localStorage.getItem('admin_token');
      const storedUser = localStorage.getItem('admin_user');

      if (storedToken && storedUser) {
        // Verify admin token is still valid
        const backendUrl = getBackendURL();
        const response = await fetch(`${backendUrl}/api/auth/check`, {
          headers: {
            'Authorization': `Bearer ${storedToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.authenticated) {
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          } else {
            // Token invalid, clear storage
            clearAuth();
          }
        } else {
          // Token invalid, clear storage
          clearAuth();
        }
      }

      // Check customer auth
      const storedCustomerToken = localStorage.getItem('customer_token');
      const storedCustomer = localStorage.getItem('customer_user');

      console.log('CheckAuth - Customer tokens found:', { 
        hasToken: !!storedCustomerToken, 
        hasUser: !!storedCustomer,
        tokenPreview: storedCustomerToken ? storedCustomerToken.substring(0, 20) + '...' : 'none'
      });

      if (storedCustomerToken && storedCustomer) {
        // Verify customer token is still valid
        const backendUrl = getBackendURL();
        const response = await fetch(`${backendUrl}/api/customer-auth/check`, {
          headers: {
            'Authorization': `Bearer ${storedCustomerToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Customer auth check response:', data);
          if (data.authenticated) {
            console.log('Customer authenticated, setting state');
            setCustomerToken(storedCustomerToken);
            setCustomer(JSON.parse(storedCustomer));
          } else {
            console.log('Customer not authenticated, clearing storage');
            // Token invalid, clear storage
            clearCustomerAuth();
          }
        } else {
          // Token invalid, clear storage
          clearCustomerAuth();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuth();
      clearCustomerAuth();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      
      // Store auth data
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      
      setToken(data.access_token);
      setUser(data.user);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const customerLogin = async (email, password) => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/customer-auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      
      // Store customer auth data
      localStorage.setItem('customer_token', data.access_token);
      localStorage.setItem('customer_user', JSON.stringify(data.user));
      
      console.log('Customer login success, setting tokens:', { token: data.access_token, user: data.user });
      setCustomerToken(data.access_token);
      setCustomer(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const customerRegister = async (name, email, password, phone, city, state) => {
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/customer-auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password, phone, city, state }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();
      
      // Store customer auth data
      localStorage.setItem('customer_token', data.access_token);
      localStorage.setItem('customer_user', JSON.stringify(data.user));
      
      setCustomerToken(data.access_token);
      setCustomer(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        // Call admin logout endpoint
        const backendUrl = getBackendURL();
        await fetch(`${backendUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Admin logout API call failed:', error);
    } finally {
      clearAuth();
    }
  };

  const customerLogout = async () => {
    try {
      if (customerToken) {
        // Call customer logout endpoint
        const backendUrl = getBackendURL();
        await fetch(`${backendUrl}/api/customer-auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${customerToken}`
          }
        });
      }
    } catch (error) {
      console.error('Customer logout API call failed:', error);
    } finally {
      clearCustomerAuth();
    }
  };

  const clearAuth = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setToken(null);
    setUser(null);
  };

  const clearCustomerAuth = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    setCustomerToken(null);
    setCustomer(null);
  };

  const isAuthenticated = () => {
    return !!(token && user);
  };

  const isCustomerAuthenticated = () => {
    return !!(customerToken && customer);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const getAuthHeaders = () => {
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
    return {};
  };

  const getCustomerAuthHeaders = () => {
    if (customerToken) {
      return {
        'Authorization': `Bearer ${customerToken}`,
        'Content-Type': 'application/json'
      };
    }
    return {};
  };

  const value = {
    user,
    customer,
    token,
    customerToken,
    loading,
    login,
    logout,
    customerLogin,
    customerRegister,
    customerLogout,
    isAuthenticated,
    isCustomerAuthenticated,
    isAdmin,
    getAuthHeaders,
    getCustomerAuthHeaders,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;